// reusable photo strip — thumbnails + an add-photo button. presentational: the parent owns the
// usePhotos/useUploadPhoto/useDeletePhoto hooks and passes the filtered list + handlers, so it stays cheap
// when rendered across many rows. Deleting always asks first (a photo can't be brought back).
//   mouse — 32px thumbnails that open full size in a new tab, a corner × to delete, one camera button.
//   touch — 56px thumbnails in a sideways strip; a tap opens the photo in an in-app sheet (Open full size,
//           Delete), so there is no × to hit by mistake. Two 44px buttons lead the strip: Take photo
//           (straight to the rear camera) and Choose a photo (library / files).
import { memo, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import type { PhotoMeta } from '../features/hooks.js';
import { useIsCoarse } from '../lib/media.js';
import { Modal, useConfirm } from './ui.js';
import { IconCamera, IconTrash, IconUpload } from './icons.js';

export const EMPTY_PHOTOS: PhotoMeta[] = [];

const fileUrl = (p: PhotoMeta) => `/api/photos/file?id=${p.id}`;

export const PhotoStrip = memo(function PhotoStrip({
  photos,
  canWrite,
  busy,
  onUpload,
  onDelete,
  deleteMessage,
}: {
  photos: PhotoMeta[];
  canWrite: boolean;
  busy?: boolean;
  onUpload: (file: File) => void;
  /** called once the user has confirmed the delete */
  onDelete: (id: string) => void;
  /** the confirm's text, e.g. `Delete this photo from “Anchor”?`; defaults to naming the file */
  deleteMessage?: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const coarse = useIsCoarse();
  const [viewing, setViewing] = useState<PhotoMeta | null>(null);
  const [confirmNode, confirm] = useConfirm();

  const remove = async (p: PhotoMeta) => {
    const ok = await confirm({
      title: 'Delete photo',
      message: deleteMessage ?? `Delete “${p.filename}”? It can’t be restored.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    onDelete(p.id);
    setViewing(null);
  };
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onUpload(f);
    e.target.value = '';
  };

  // touch: the take / choose buttons lead the strip, so they stay in reach however many photos follow
  const addButtons = canWrite && (
    <>
      <input ref={fileRef} className="pstrip-file" type="file" accept="image/*" onChange={pick} tabIndex={-1} aria-hidden="true" />
      {coarse && (
        <>
          <input ref={camRef} className="pstrip-file" type="file" accept="image/*" capture="environment" onChange={pick} tabIndex={-1} aria-hidden="true" />
          <button type="button" className="btn btn-sm pstrip-add" disabled={busy} onClick={() => camRef.current?.click()} title="Take photo" aria-label="Take photo">
            {busy ? '…' : <IconCamera size={18} />}
          </button>
        </>
      )}
      <button
        className="btn btn-sm pstrip-add"
        disabled={busy}
        onClick={() => fileRef.current?.click()}
        title={coarse ? 'Choose a photo' : 'Add photo'}
        aria-label={coarse ? 'Choose a photo' : undefined}
      >
        {coarse ? <IconUpload size={18} /> : busy ? '…' : <IconCamera size={15} />}
      </button>
    </>
  );

  return (
    <div className="pstrip">
      {coarse && addButtons}
      {photos.map((p) => (coarse ? (
        <button key={p.id} type="button" className="pstrip-thumb" onClick={() => setViewing(p)} aria-label={`Open photo ${p.filename}`}>
          <img className="pstrip-img" src={fileUrl(p)} alt="" loading="lazy" />
        </button>
      ) : (
        <span key={p.id} className="pstrip-item">
          <a href={fileUrl(p)} target="_blank" rel="noopener noreferrer" title={p.filename}>
            <img className="pstrip-img" src={fileUrl(p)} alt={p.filename} loading="lazy" />
          </a>
          {canWrite && (
            <button type="button" className="pstrip-del" onClick={() => void remove(p)} title="Delete photo" aria-label={`Delete photo ${p.filename}`}>
              ×
            </button>
          )}
        </span>
      )))}
      {!coarse && addButtons}
      {viewing && createPortal(
        <Modal
          sheet
          dataTitle
          title={viewing.filename || 'Photo'}
          onClose={() => setViewing(null)}
          closeLabel="Close photo"
          footer={
            <>
              <a className="btn" href={fileUrl(viewing)} target="_blank" rel="noopener noreferrer">Open full size</a>
              {canWrite && (
                <button type="button" className="btn btn-danger" onClick={() => void remove(viewing)}>
                  <IconTrash size={15} /> Delete
                </button>
              )}
            </>
          }
        >
          <img className="pstrip-full" src={fileUrl(viewing)} alt={viewing.filename} />
        </Modal>,
        document.body,
      )}
      {confirmNode && createPortal(confirmNode, document.body)}
    </div>
  );
});
